import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import { Box, InputBase, List, ListItemButton, Typography } from '@mui/material';
import { Check, Search } from 'lucide-react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { setActiveManagerOrg } from 'store/actions/User';
import { userTypes } from 'utils/constants';
import { markOrgSelectorSwitch } from 'utils/org-switch-intent';
import { getSwitchableOrgs } from 'utils/helpers/org-switcher.helper';

// Below this many orgs the whole list fits at a glance and a search field is only noise.
const SEARCH_THRESHOLD = 6;

const ellipsis = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};

const OrgRow = ({ org, isActive, disabled, onClick }) => (
    <ListItemButton
        selected={isActive}
        disabled={disabled}
        aria-current={isActive ? 'true' : undefined}
        onClick={onClick}
        sx={{
            px: '20px',
            py: '8px',
            gap: '10px',
            '&:hover': { bgcolor: '#f9f9f9' },
            '&.Mui-selected, &.Mui-selected:hover': {
                bgcolor: '#f3fbf7',
                color: '#212322'
            }
        }}
    >
        <Typography
            sx={{
                flex: 1,
                fontSize: '13px',
                fontWeight: isActive ? 600 : 400,
                color: '#212322',
                ...ellipsis
            }}
            title={org.org_name}
        >
            {org.org_name}
        </Typography>
        {isActive && <Check size={16} color="#33c78f" style={{ flexShrink: 0 }} />}
    </ListItemButton>
);

OrgRow.propTypes = {
    org: PropTypes.object.isRequired,
    isActive: PropTypes.bool,
    disabled: PropTypes.bool,
    onClick: PropTypes.func
};

// The orgs the user can switch to, in the profile menu of the operator app. The active one is
// pinned on top, outside the search, so it stays in view however long the list is; picking
// another switches to it. Switching is locked while the user is on a call.
const OrgList = ({ orgs, userData, activeOrg, locked, onSwitch }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [filterValue, setFilterValue] = useState('');

    const switchableOrgs = useMemo(() => {
        if (userData?.type === userTypes.SUPER_ADMIN) {
            return (orgs || []).filter(org => org.org_id === userData.active_org_id);
        }

        return getSwitchableOrgs(orgs, userData) || [];
    }, [orgs, userData]);

    const currentOrg =
        switchableOrgs.find(org => org.org_id === activeOrg?.org_id) ||
        (activeOrg?.org_name ? activeOrg : null);

    const otherOrgs = switchableOrgs.filter(org => org.org_id !== currentOrg?.org_id);

    const filteredOrgs = otherOrgs.filter(org =>
        (org.org_name || '').toLowerCase().includes(filterValue.trim().toLowerCase())
    );

    const handleSelect = org => {
        if (activeOrg?.org_id === org.org_id) return;

        // Leave the page now: it may not exist in the new org. The root waits for the switch
        // to land, then opens the first page that org has.
        markOrgSelectorSwitch(org.org_id);
        navigate('/');
        dispatch(setActiveManagerOrg({ ...org, origin: 'org selector' }));
        onSwitch?.();
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '20px',
                    pt: '12px',
                    pb: '6px'
                }}
            >
                <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#8c8c8c' }}>
                    Organizations
                </Typography>
                {switchableOrgs.length > 1 && (
                    <Typography sx={{ fontSize: '12px', color: '#bfbfbf' }}>
                        {switchableOrgs.length}
                    </Typography>
                )}
            </Box>

            {currentOrg && (
                <List component="div" sx={{ p: 0, flexShrink: 0 }}>
                    <OrgRow org={currentOrg} isActive onClick={() => {}} />
                </List>
            )}

            {otherOrgs.length > 0 && (
                <>
                    {otherOrgs.length > SEARCH_THRESHOLD && (
                        <Box sx={{ px: '20px', pt: '10px', pb: '8px', flexShrink: 0 }}>
                            <InputBase
                                fullWidth
                                placeholder="Search organization"
                                value={filterValue}
                                onChange={e => setFilterValue(e.target.value)}
                                // Typing a letter must not move focus to a list item.
                                onKeyDown={e => e.stopPropagation()}
                                inputProps={{ 'aria-label': 'Search organization' }}
                                startAdornment={
                                    <Search
                                        size={14}
                                        color="#8c8c8c"
                                        style={{ flexShrink: 0, marginRight: 8 }}
                                    />
                                }
                                sx={{
                                    height: 32,
                                    px: '10px',
                                    fontSize: '13px',
                                    color: '#212322',
                                    border: '1px solid #d9d9d9',
                                    borderRadius: '8px',
                                    transition: 'border-color 150ms ease',
                                    '&:hover': { borderColor: '#bfbfbf' },
                                    '&.Mui-focused': { borderColor: '#33c78f' },
                                    '& input::placeholder': {
                                        color: '#bfbfbf',
                                        opacity: 1
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {locked && (
                        <Typography
                            sx={{
                                px: '20px',
                                pt: '6px',
                                pb: '6px',
                                fontSize: '12px',
                                color: '#8c8c8c'
                            }}
                        >
                            You can switch organizations once your call ends.
                        </Typography>
                    )}

                    <List
                        component="nav"
                        aria-label="Switch organization"
                        sx={{
                            p: 0,
                            pt: '4px',
                            pb: '6px',
                            overflowY: 'auto',
                            minHeight: 0
                        }}
                    >
                        {filteredOrgs.length > 0 ? (
                            filteredOrgs.map(org => (
                                <OrgRow
                                    key={org.org_id}
                                    org={org}
                                    disabled={locked}
                                    onClick={() => handleSelect(org)}
                                />
                            ))
                        ) : (
                            <Typography
                                sx={{
                                    px: '20px',
                                    py: '12px',
                                    fontSize: '13px',
                                    color: '#8c8c8c',
                                    textAlign: 'center'
                                }}
                            >
                                No organizations found
                            </Typography>
                        )}
                    </List>
                </>
            )}
        </Box>
    );
};

OrgList.propTypes = {
    orgs: PropTypes.array,
    userData: PropTypes.object,
    activeOrg: PropTypes.object,
    // True while the user is on a call: they stay in the org that call belongs to.
    locked: PropTypes.bool,
    // Called after a switch to another org has been asked for.
    onSwitch: PropTypes.func
};

const mapStateToProps = ({ org, user }) => {
    const { orgs } = org;
    const { userData, activeOrg } = user;
    return { orgs, userData, activeOrg };
};

export default connect(mapStateToProps, {})(OrgList);
